import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, throwError, timer, of } from 'rxjs';
import { catchError, switchMap, takeUntil, tap, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Process, ProcessListResponse } from '../models/process.model';
import {
  Notification,
  NotificationListFilters,
  NotificationListResponse,
  UnreadCountResponse,
  MarkNotificationReadResponse,
  MarkAllNotificationsReadResponse,
  QuestionnaireNotificationContext
} from '../models/notification.model';

@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  // ==========================================================================
  // PROPRIEDADES
  // ==========================================================================

  private readonly apiUrl = environment.apiUrl;
  private readonly pollIntervalMs = 60000;
  private readonly fallbackReadStorageKey = 'nunciatura.notifications.read-fallback-ids';
  private readonly useNotificationsApi = environment.notificationsApiEnabled ?? false;

  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private destroy$ = new Subject<void>();
  private pollDestroy$ = new Subject<void>();
  private pollingStarted = false;
  private notificationsCache: Notification[] = [];

  // ==========================================================================
  // OBSERVABLES PÚBLICOS
  // ==========================================================================

  public notifications$ = this.notificationsSubject.asObservable();
  public unreadCount$ = this.unreadCountSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  // ==========================================================================
  // CONSTRUCTOR
  // ==========================================================================

  constructor(private http: HttpClient) {}

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  ngOnDestroy(): void {
    this.stopPolling();
    this.destroy$.next();
    this.destroy$.complete();
    this.pollDestroy$.complete();
  }

  // ==========================================================================
  // MÉTODOS CRUD - READ/LIST
  // ==========================================================================

  /**
   * Obtém a lista paginada de notificações
   * @param filters Filtros opcionais para a busca
   * @returns Observable com a resposta da API
   */
  public getNotifications(filters: NotificationListFilters = {}): Observable<NotificationListResponse> {
    this.loadingSubject.next(true);

    if (!this.useNotificationsApi) {
      return this.buildFallbackNotificationsResponse(filters).pipe(
        tap(() => this.loadingSubject.next(false))
      );
    }

    const params = new HttpParams().set('page', '1').set('per_page', '500');

    return this.http.get<NotificationListResponse>(`${this.apiUrl}/notifications`, { params })
      .pipe(
        map((response) => this.normalizeNotificationsResponse(response, filters)),
        tap((response) => {
          if (response.success) {
            this.unreadCountSubject.next(
              this.notificationsCache.filter((item) => !item.read_at).length
            );
          }
          this.loadingSubject.next(false);
        }),
        catchError(() => this.buildFallbackNotificationsResponse(filters))
      );
  }

  /**
   * Obtém a contagem de notificações não lidas
   * @returns Observable com a contagem
   */
  public getUnreadCount(): Observable<UnreadCountResponse> {
    if (!this.useNotificationsApi) {
      return this.fetchProcessesNotificationsTotal().pipe(
        map((count) => ({
          success: true,
          data: { count }
        }))
      );
    }

    return this.http.get<UnreadCountResponse>(`${this.apiUrl}/notifications/unread-count`).pipe(
      tap((response) => {
        if (response.success) {
          this.unreadCountSubject.next(response.data?.count ?? 0);
        }
      }),
      catchError(() =>
        this.fetchProcessesNotificationsTotal().pipe(
          map((count) => ({
            success: true,
            data: { count }
          }))
        )
      )
    );
  }

  // ==========================================================================
  // MÉTODOS CRUD - UPDATE
  // ==========================================================================

  /**
   * Marca uma notificação como lida
   * @param id ID da notificação
   * @returns Observable com a resposta da API
   */
  public markAsRead(id: string): Observable<MarkNotificationReadResponse> {
    if (!this.useNotificationsApi) {
      return this.markFallbackNotificationAsRead(id);
    }

    return this.http.patch<MarkNotificationReadResponse>(
      `${this.apiUrl}/notifications/${encodeURIComponent(id)}/read`,
      {}
    ).pipe(
      tap((response) => {
        if (response.success) {
          this.saveReadFallbackEntry(id, response.data?.read_at ?? new Date().toISOString());
          this.updateLocalNotificationReadState(id, response.data?.read_at ?? undefined);
          this.decrementUnreadCount();
        }
      }),
      catchError((error) => {
        if (this.isFallbackNotificationId(id)) {
          return this.markFallbackNotificationAsRead(id);
        }
        return this.handleError(error);
      })
    );
  }

  /**
   * Marca todas as notificações como lidas
   * @returns Observable com a resposta da API
   */
  public markAllAsRead(): Observable<MarkAllNotificationsReadResponse> {
    if (!this.useNotificationsApi) {
      return this.markAllFallbackNotificationsAsRead();
    }

    return this.http.patch<MarkAllNotificationsReadResponse>(`${this.apiUrl}/notifications/mark-all-read`, {})
      .pipe(
        tap((response) => {
          if (response.success) {
            const readAt = new Date().toISOString();
            const current = this.notificationsSubject.value.map((n) => ({
              ...n,
              read_at: n.read_at ?? readAt
            }));
            const cache = this.notificationsCache.map((n) => ({
              ...n,
              read_at: n.read_at ?? readAt
            }));

            current.forEach((notification) => {
              this.saveReadFallbackEntry(notification.id, notification.read_at || readAt);
            });

            this.setNotificationsCache(cache.length > 0 ? cache : current);
            this.notificationsSubject.next(current);
            this.unreadCountSubject.next(0);
          }
        }),
        catchError(() => this.markAllFallbackNotificationsAsRead())
      );
  }

  // ==========================================================================
  // MÉTODOS AUXILIARES
  // ==========================================================================

  /**
   * Inicia polling periódico da contagem de não lidas
   */
  public startPolling(): void {
    if (this.pollingStarted) {
      return;
    }

    this.pollingStarted = true;
    this.pollDestroy$ = new Subject<void>();
    this.triggerUnreadCountRefresh();

    timer(this.pollIntervalMs, this.pollIntervalMs)
      .pipe(
        takeUntil(this.pollDestroy$),
        switchMap(() => this.refreshUnreadCount())
      )
      .subscribe();
  }

  /**
   * Para o polling de notificações
   */
  public stopPolling(): void {
    this.pollingStarted = false;
    this.pollDestroy$.next();
  }

  /**
   * Atualiza a contagem de não lidas manualmente
   */
  public refreshUnreadCount(): Observable<number> {
    if (!this.useNotificationsApi) {
      return this.fetchProcessesNotificationsTotal();
    }

    return this.http.get<UnreadCountResponse>(`${this.apiUrl}/notifications/unread-count`).pipe(
      map((response) => (response.success ? response.data?.count ?? 0 : 0)),
      tap((count) => this.unreadCountSubject.next(count)),
      catchError(() => this.fetchProcessesNotificationsTotal())
    );
  }

  /**
   * Atualiza o cache de notificações para badges em listagens
   */
  public refreshNotificationsCache(): void {
    this.refreshUnreadCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  /**
   * Dispara atualização da contagem de não lidas
   */
  public triggerUnreadCountRefresh(): void {
    this.refreshUnreadCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  /**
   * Carrega as últimas notificações para o dropdown do header
   * @param limit Quantidade máxima de notificações
   */
  public loadRecentNotifications(limit = 5): void {
    this.getNotifications({ page: 1, per_page: limit, read: false })
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  /**
   * Verifica se a notificação foi gerada pelo fallback local
   * @param id ID da notificação
   */
  public isFallbackNotificationId(id: string): boolean {
    return id.startsWith('fallback-process-') || id.startsWith('fallback-answer-');
  }

  /**
   * Verifica se a notificação deve navegar para a listagem de grupos
   * @param notification Notificação selecionada
   */
  public shouldNavigateToProcessGroups(notification: Notification): boolean {
    return notification.id.startsWith('fallback-process-')
      || !notification.metadata.group_id
      || !notification.metadata.questionnaire_id;
  }

  /**
   * Marca como lida a notificação relacionada à visualização de respostas do questionário
   * @param context Identificadores do processo, grupo, questionário e informador
   */
  public markAsReadForQuestionnaireView(
    context: QuestionnaireNotificationContext
  ): Observable<MarkNotificationReadResponse | null> {
    const existing = this.findNotificationByContext(context);

    if (existing && !existing.read_at) {
      return this.markAsRead(existing.id);
    }

    if (existing?.read_at) {
      return of(null);
    }

    if (context.answer_id) {
      const answerNotificationId = this.buildFallbackAnswerNotificationId(context.answer_id);

      if (this.getReadFallbackIds().includes(answerNotificationId)) {
        return of(null);
      }

      return this.markFallbackNotificationAsRead(answerNotificationId);
    }

    if (!this.useNotificationsApi) {
      const fallbackId = this.buildFallbackProcessNotificationId(context.process_id);

      if (this.getReadFallbackIds().includes(fallbackId)) {
        return of(null);
      }

      return this.markFallbackNotificationAsRead(fallbackId);
    }

    return this.getNotifications({ page: 1, per_page: 100, read: false }).pipe(
      switchMap(() => {
        const notification = this.findNotificationByContext(context);

        if (notification && !notification.read_at) {
          return this.markAsRead(notification.id);
        }

        return of(null);
      })
    );
  }

  /**
   * Retorna a contagem de notificações não lidas de um processo
   * @param process Processo
   */
  public getUnreadCountForProcess(process: Process): number {
    const unreadForProcess = this.notificationsCache.filter(
      (notification) => notification.metadata.process_id === process.id && !notification.read_at
    ).length;

    if (this.notificationsCache.length > 0) {
      return unreadForProcess;
    }

    if (this.isProcessNotificationRead(process.id)) {
      return 0;
    }

    return this.getProcessNotificationsCount(process);
  }

  /**
   * Retorna a contagem de notificações não lidas de um grupo
   * @param group Grupo
   * @param processId ID do processo vinculado ao grupo
   */
  public getUnreadCountForGroup(
    group: { id?: string; notifications_count?: number | null; answers_received?: number | null },
    processId: string
  ): number {
    const unreadForGroup = this.notificationsCache.filter(
      (notification) =>
        notification.metadata.process_id === processId &&
        notification.metadata.group_id === group.id &&
        !notification.read_at
    ).length;

    if (this.notificationsCache.length > 0) {
      return unreadForGroup;
    }

    if (this.isProcessNotificationRead(processId)) {
      return 0;
    }

    if (group.notifications_count !== undefined && group.notifications_count !== null) {
      return group.notifications_count;
    }

    return group.answers_received ?? 0;
  }

  /**
   * Verifica se a notificação fallback do processo já foi lida
   * @param processId ID do processo
   */
  public isProcessNotificationRead(processId: string): boolean {
    const fallbackId = this.buildFallbackProcessNotificationId(processId);
    return this.getReadFallbackIds().includes(fallbackId);
  }

  /**
   * Verifica se a notificação foi lida (API ou persistência local)
   * @param notification Notificação
   */
  public isNotificationRead(notification: Notification): boolean {
    return !!notification.read_at || notification.id in this.getReadFallbackEntries();
  }

  /**
   * Retorna a contagem atual de não lidas
   */
  public getCurrentUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  /**
   * Limpa o estado das notificações
   */
  public clearNotifications(): void {
    this.notificationsSubject.next([]);
    this.notificationsCache = [];
    this.unreadCountSubject.next(0);
  }

  // ==========================================================================
  // MÉTODOS PRIVADOS
  // ==========================================================================

  /**
   * Atualiza o estado local de uma notificação como lida
   * @param id ID da notificação
   */
  private updateLocalNotificationReadState(id: string, readAt?: string): void {
    const resolvedReadAt = readAt ?? this.getReadFallbackEntries()[id] ?? new Date().toISOString();

    const applyUpdate = (notifications: Notification[]): Notification[] => {
      const index = notifications.findIndex((notification) => notification.id === id);

      if (index === -1) {
        return notifications;
      }

      const updated = [...notifications];
      updated[index] = {
        ...updated[index],
        read_at: updated[index].read_at ?? resolvedReadAt
      };

      return updated;
    };

    this.notificationsCache = applyUpdate(this.notificationsCache);
    this.notificationsSubject.next(applyUpdate(this.notificationsSubject.value));
  }

  /**
   * Decrementa a contagem de não lidas localmente
   */
  private decrementUnreadCount(): void {
    const current = this.unreadCountSubject.value;
    if (current > 0) {
      this.unreadCountSubject.next(current - 1);
    }
  }

  /**
   * Fallback quando a API de notificações não estiver disponível:
   * soma os questionários respondidos de todos os processos
   */
  private fetchProcessesNotificationsTotal(): Observable<number> {
    const params = new HttpParams()
      .set('page', '1')
      .set('per_page', '500');

    return this.http.get<ProcessListResponse>(`${this.apiUrl}/processes`, { params })
      .pipe(
        map((response) => {
          if (!response.success || !response.data) {
            return 0;
          }
          return this.sumProcessNotifications(response.data);
        }),
        tap((total) => this.unreadCountSubject.next(total)),
        catchError(() => {
          this.unreadCountSubject.next(0);
          return of(0);
        })
      );
  }

  /**
   * Soma o total de questionários respondidos dos processos
   * @param processes Lista de processos
   */
  private sumProcessNotifications(processes: Process[]): number {
    const readIds = this.getReadFallbackIds();

    return processes.reduce((total, process) => {
      const fallbackId = this.buildFallbackProcessNotificationId(process.id);
      if (readIds.includes(fallbackId)) {
        return total;
      }
      return total + this.getProcessNotificationsCount(process);
    }, 0);
  }

  /**
   * Retorna a contagem de notificações de um processo
   * @param process Processo
   */
  private getProcessNotificationsCount(process: Process): number {
    if (process.notifications_count !== undefined && process.notifications_count !== null) {
      return process.notifications_count;
    }
    return process.answers?.received ?? 0;
  }

  /**
   * Monta resposta de notificações a partir dos processos
   * @param filters Filtros de listagem
   */
  private buildFallbackNotificationsResponse(
    filters: NotificationListFilters
  ): Observable<NotificationListResponse> {
    const params = new HttpParams()
      .set('page', '1')
      .set('per_page', '500');

    return this.http.get<ProcessListResponse>(`${this.apiUrl}/processes`, { params })
      .pipe(
        map((response) => {
          const processes = response.success && response.data ? response.data : [];
          let notifications = this.buildFallbackNotificationsFromProcesses(processes);

          if (filters.read === false) {
            notifications = notifications.filter((notification) => !notification.read_at);
          } else if (filters.read === true) {
            notifications = notifications.filter((notification) => !!notification.read_at);
          }

          const page = filters.page ?? 1;
          const perPage = filters.per_page ?? 15;
          const total = notifications.length;
          const lastPage = Math.max(1, Math.ceil(total / perPage));
          const start = (page - 1) * perPage;
          const paginated = notifications.slice(start, start + perPage);

          this.notificationsSubject.next(paginated);
          this.unreadCountSubject.next(this.sumProcessNotifications(processes));

          return {
            success: true,
            data: paginated,
            pagination: {
              current_page: page,
              per_page: perPage,
              total,
              last_page: lastPage,
              from: total === 0 ? 0 : start + 1,
              to: Math.min(start + perPage, total),
              has_more_pages: page < lastPage
            }
          };
        }),
        catchError(() => {
          this.notificationsSubject.next([]);
          this.unreadCountSubject.next(0);
          return of({
            success: true,
            data: [],
            pagination: {
              current_page: 1,
              per_page: filters.per_page ?? 15,
              total: 0,
              last_page: 1,
              from: 0,
              to: 0,
              has_more_pages: false
            }
          });
        })
      );
  }

  /**
   * Converte processos com respostas em notificações locais
   * @param processes Lista de processos
   */
  private buildFallbackNotificationsFromProcesses(processes: Process[]): Notification[] {
    const readIds = this.getReadFallbackIds();

    return processes
      .map((process) => ({
        process,
        count: this.getProcessNotificationsCount(process)
      }))
      .filter(({ count }) => count > 0)
      .sort(
        (a, b) =>
          new Date(b.process.updated_at).getTime() - new Date(a.process.updated_at).getTime()
      )
      .map(({ process, count }) => {
        const id = this.buildFallbackProcessNotificationId(process.id);
        const isRead = readIds.includes(id);

        return {
          id,
          type: 'questionnaire_submitted' as const,
          title: process.name,
          message: `${count} ${count === 1 ? 'questionário respondido' : 'questionários respondidos'} aguardando avaliação`,
          read_at: isRead ? process.updated_at : null,
          created_at: process.updated_at,
          metadata: {
            process_id: process.id,
            process_name: process.name,
            group_id: '',
            group_name: '',
            informer_id: '',
            questionnaire_id: ''
          }
        };
      });
  }

  /**
   * Marca notificação fallback como lida localmente
   * @param id ID da notificação fallback
   */
  private markFallbackNotificationAsRead(id: string): Observable<MarkNotificationReadResponse> {
    const readAt = new Date().toISOString();
    this.saveReadFallbackEntry(id, readAt);
    this.updateLocalNotificationReadState(id, readAt);
    this.triggerUnreadCountRefresh();

    const notification = this.notificationsSubject.value.find((item) => item.id === id);

    return of({
      success: true,
      message: 'Notificação marcada como lida',
      data: notification ?? {
        id,
        type: 'questionnaire_submitted',
        title: '',
        message: '',
        read_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        metadata: {
          process_id: '',
          group_id: '',
          informer_id: '',
          questionnaire_id: ''
        }
      }
    });
  }

  /**
   * Marca todas as notificações fallback como lidas
   */
  private markAllFallbackNotificationsAsRead(): Observable<MarkAllNotificationsReadResponse> {
    const finalizeMarkAll = (ids: string[]): MarkAllNotificationsReadResponse => {
      const readAt = new Date().toISOString();
      ids.forEach((id) => this.saveReadFallbackEntry(id, readAt));

      const updated = this.notificationsCache.map((notification) => ({
        ...notification,
        read_at: notification.read_at ?? readAt
      }));

      this.setNotificationsCache(updated);
      this.unreadCountSubject.next(0);

      return {
        success: true,
        message: 'Notificações marcadas como lidas',
        data: {
          updated_count: ids.length
        }
      };
    };

    if (this.useNotificationsApi) {
      return this.http.get<NotificationListResponse>(`${this.apiUrl}/notifications`, {
        params: new HttpParams().set('page', '1').set('per_page', '500')
      }).pipe(
        map((response) => {
          const notifications = this.applyReadStateToNotifications(
            response.success && response.data ? response.data : []
          );
          this.setNotificationsCache(notifications);
          return finalizeMarkAll(notifications.map((notification) => notification.id));
        })
      );
    }

    const current = this.notificationsCache.length > 0
      ? this.notificationsCache
      : this.notificationsSubject.value;

    return of(finalizeMarkAll(current.map((notification) => notification.id)));
  }

  /**
   * Localiza notificação pelo contexto do questionário visualizado
   * @param context Identificadores do processo, grupo, questionário e informador
   */
  private findNotificationByContext(
    context: QuestionnaireNotificationContext
  ): Notification | undefined {
    return this.notificationsSubject.value.find(
      (notification) =>
        notification.metadata.process_id === context.process_id &&
        notification.metadata.group_id === context.group_id &&
        notification.metadata.questionnaire_id === context.questionnaire_id &&
        notification.metadata.informer_id === context.informer_id
    );
  }

  /**
   * Atualiza cache interno e observable de notificações
   * @param notifications Lista completa de notificações
   */
  private setNotificationsCache(notifications: Notification[]): void {
    this.notificationsCache = notifications;
    this.notificationsSubject.next(notifications);
  }

  /**
   * Normaliza resposta da API aplicando estado de leitura local e filtros
   * @param response Resposta da API
   * @param filters Filtros de listagem
   */
  private normalizeNotificationsResponse(
    response: NotificationListResponse,
    filters: NotificationListFilters
  ): NotificationListResponse {
    let notifications = this.applyReadStateToNotifications(
      response.success && response.data ? response.data : []
    );

    if (filters.read === false) {
      notifications = notifications.filter((notification) => !notification.read_at);
    } else if (filters.read === true) {
      notifications = notifications.filter((notification) => !!notification.read_at);
    }

    this.setNotificationsCache(
      this.applyReadStateToNotifications(
        response.success && response.data ? response.data : []
      )
    );

    const page = filters.page ?? 1;
    const perPage = filters.per_page ?? 15;
    const total = notifications.length;
    const lastPage = Math.max(1, Math.ceil(total / perPage));
    const start = (page - 1) * perPage;
    const paginated = notifications.slice(start, start + perPage);

    return {
      success: true,
      data: paginated,
      pagination: {
        current_page: page,
        per_page: perPage,
        total,
        last_page: lastPage,
        from: total === 0 ? 0 : start + 1,
        to: Math.min(start + perPage, total),
        has_more_pages: page < lastPage
      }
    };
  }

  /**
   * Aplica estado de leitura persistido localmente às notificações
   * @param notifications Lista de notificações
   */
  private applyReadStateToNotifications(notifications: Notification[]): Notification[] {
    const entries = this.getReadFallbackEntries();

    return notifications.map((notification) => {
      if (!(notification.id in entries) && !notification.read_at) {
        return notification;
      }

      const storedReadAt = entries[notification.id];

      return {
        ...notification,
        read_at: notification.read_at ?? (storedReadAt || null)
      };
    });
  }

  /**
   * Gera ID estável para notificação fallback de processo
   * @param processId ID do processo
   */
  private buildFallbackProcessNotificationId(processId: string): string {
    return `fallback-process-${processId}`;
  }

  /**
   * Gera ID estável para notificação fallback de resposta
   * @param answerId ID da resposta do questionário
   */
  private buildFallbackAnswerNotificationId(answerId: string): string {
    return `fallback-answer-${answerId}`;
  }

  /**
   * Obtém mapa de notificações lidas localmente (id -> read_at ISO)
   */
  private getReadFallbackEntries(): Record<string, string> {
    try {
      const raw = localStorage.getItem(this.fallbackReadStorageKey);
      if (!raw) {
        return {};
      }

      const parsed = JSON.parse(raw) as Record<string, string> | string[];

      if (Array.isArray(parsed)) {
        return Object.fromEntries(parsed.map((id) => [id, '']));
      }

      if (parsed && typeof parsed === 'object') {
        return parsed;
      }

      return {};
    } catch {
      return {};
    }
  }

  /**
   * Persiste data de leitura de uma notificação fallback
   * @param id ID da notificação
   * @param readAt Data/hora da leitura (ISO)
   */
  private saveReadFallbackEntry(id: string, readAt: string): void {
    const entries = this.getReadFallbackEntries();

    if (!entries[id]) {
      entries[id] = readAt;
      this.saveReadFallbackEntries(entries);
    }
  }

  /**
   * Persiste mapa de notificações lidas
   * @param entries Mapa id -> read_at
   */
  private saveReadFallbackEntries(entries: Record<string, string>): void {
    try {
      localStorage.setItem(this.fallbackReadStorageKey, JSON.stringify(entries));
    } catch {
      // Ignora falhas de quota ou acesso ao storage
    }
  }

  /**
   * Obtém IDs de notificações fallback já lidas
   */
  private getReadFallbackIds(): string[] {
    return Object.keys(this.getReadFallbackEntries());
  }

  /**
   * @deprecated Use saveReadFallbackEntry
   */
  private saveReadFallbackIds(ids: string[]): void {
    const entries = this.getReadFallbackEntries();
    const readAt = new Date().toISOString();

    ids.forEach((id) => {
      if (!entries[id]) {
        entries[id] = readAt;
      }
    });

    this.saveReadFallbackEntries(entries);
  }

  /**
   * Manipulador de erros centralizado
   * @param error Erro HTTP
   * @returns Observable que lança o erro
   */
  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let errorMessage = 'Erro desconhecido';

    if (error.error && typeof error.error === 'object') {
      errorMessage = error.error.message || 'Erro ao processar requisição';
    } else {
      switch (error.status) {
        case 400:
          errorMessage = 'Requisição inválida';
          break;
        case 401:
          errorMessage = 'Não autorizado';
          break;
        case 403:
          errorMessage = 'Acesso negado';
          break;
        case 404:
          errorMessage = 'Recurso não encontrado';
          break;
        case 422:
          errorMessage = 'Dados de validação inválidos';
          break;
        case 500:
          errorMessage = 'Erro interno do servidor';
          break;
        default:
          errorMessage = `Erro: ${error.status}`;
      }
    }

    return throwError(() => new Error(errorMessage));
  };
}
